import { useEffect, useRef } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { getLocalStorage } from '@shared/core';
import type { UploadedPreviewImages } from '@shared/components/PreviewWithPhoto/PreviewWithPhoto.types';
import { usePreviewWithPhotoState } from '@apps/admin/components/PreviewWithPhoto/usePreviewWithPhotoState';
import { useCardTelemetry } from '@apps/card/Card.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

export const CARD_PREVIEW_IMAGE_KEY = 'cardPreviewImage';
const CARD_PREVIEW_IMAGE_CLEARED_KEY = 'cardPreviewImageCleared';
const MAX_CARD_PREVIEW_PHOTOS = 3;

const localStorage = getLocalStorage();

export const useCardPreviewWithPhoto = ({ enabled }: { enabled?: boolean } = {}) => {
  const { eventInfo } = useEventInfo();
  const { buttonInteracted } = useCardTelemetry();

  const handleCTAClick = useEventCallback((hasExisting: boolean) => {
    buttonInteracted({ name: hasExisting ? 'cardGallery_changePreviewPhotos' : 'cardGallery_viewWithYourPhoto' });
  });

  const handleClearAllPhotos = useEventCallback(() => {
    buttonInteracted({ name: 'cardGallery_clearPreviewPhotos' });
  });

  const handlePhotoSelectTelemetry = useEventCallback((photos: UploadedPreviewImages) => {
    buttonInteracted({ name: 'cardGallery_selectPhotosForPreview', count: photos.length });
  });

  const state = usePreviewWithPhotoState({
    storageKey: CARD_PREVIEW_IMAGE_KEY,
    clearedStorageKey: CARD_PREVIEW_IMAGE_CLEARED_KEY,
    enabled,
    onCTAClick: handleCTAClick,
    onClearAllPhotos: handleClearAllPhotos,
    onPhotoSelect: handlePhotoSelectTelemetry,
  });

  const { uploadedPreviewImages, media, isLoadingEventMedia, isOpenPickerDialog, setStoredPhotos } = state;

  // Auto-populate the card preview selection from the event's album. One-shot per mount with the
  // same guards as the website designer (PRNT-4433): wait for the album query to settle, don't
  // re-seed on later uploads, and bail while the picker is open. Suppressed once the user clears
  // (the cleared flag), and only runs while the feature flag is enabled.
  const hasEvaluatedAutoSeedRef = useRef(false);
  useEffect(() => {
    if (hasEvaluatedAutoSeedRef.current) return;
    if (!enabled) return;
    if (!eventInfo?.eventId) return;
    if (uploadedPreviewImages.length > 0) return;
    const clearedKey = `${CARD_PREVIEW_IMAGE_CLEARED_KEY}_${eventInfo.eventId}`;
    if (localStorage.getItem(clearedKey) === 'true') return;
    // Wait for the album query to settle before the one-shot fires.
    if (isLoadingEventMedia) return;
    // Race guard: if the picker is open when the query settles, mark this mount evaluated and bail
    // so freshly uploaded photos aren't pre-selected — the user should pick them (PRNT-4433).
    if (isOpenPickerDialog) {
      hasEvaluatedAutoSeedRef.current = true;
      return;
    }

    hasEvaluatedAutoSeedRef.current = true;

    if (media.length === 0) return;

    const autoImages: UploadedPreviewImages = media.slice(0, MAX_CARD_PREVIEW_PHOTOS).map(img => ({
      id: img.id,
      url: img.photo.url,
      height: img.photo.height,
      width: img.photo.width,
    }));
    setStoredPhotos(autoImages);
  }, [enabled, eventInfo?.eventId, uploadedPreviewImages.length, media, isLoadingEventMedia, isOpenPickerDialog, setStoredPhotos]);

  return state;
};
